import { useState } from 'react'
import {
    Alert,
    Box,
    Card,
    CardContent,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material'
import { Replay } from '@mui/icons-material'

import { useGetRetentionQuery } from '@/apis/v2Api'
import { useReportArgs } from '@/hooks/useReportArgs'
import type { Period } from '@/utils/dates'

import { ReportHeader, ReportStatus, fmtNum, fmtPct } from './common'
import { EVENT_NAME_RE } from './MatcherFields'

function cellColor(rate: number | null): string {
    if (rate === null || rate === 0) {
        return 'transparent'
    }
    return `rgba(99, 102, 241, ${(0.12 + rate * 0.78).toFixed(2)})`
}

/** v2 `/reports/retention` — cohorts by first seen (consented visitors). */
export function RetentionPage() {
    const [period, setPeriod] = useState<Period>('90d')
    const args = useReportArgs(period)
    const [bucket, setBucket] = useState<'day' | 'week'>('week')
    const [periods, setPeriods] = useState(8)
    const [event, setEvent] = useState('')
    const eventOk = event === '' || EVENT_NAME_RE.test(event)
    const { data, isFetching, error } = useGetRetentionQuery(
        { ...args, interval: bucket, periods, event: eventOk ? event || undefined : undefined },
        { skip: !eventOk },
    )

    return (
        <Box sx={{ p: 3 }}>
            <ReportHeader
                title="Retention"
                subtitle="Visitors grouped by the period they were first seen, and the share active in each later period."
                period={period}
                onPeriodChange={setPeriod}
                actions={
                    <>
                        <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={bucket}
                            onChange={(_, v: 'day' | 'week' | null) => v && setBucket(v)}
                        >
                            <ToggleButton value="day" sx={{ textTransform: 'none' }}>
                                Daily
                            </ToggleButton>
                            <ToggleButton value="week" sx={{ textTransform: 'none' }}>
                                Weekly
                            </ToggleButton>
                        </ToggleButtonGroup>
                        <TextField
                            select
                            size="small"
                            label="Periods"
                            value={periods}
                            onChange={e => setPeriods(Number(e.target.value))}
                            sx={{ width: 100 }}
                        >
                            {[4, 8, 12, 26].map(n => (
                                <MenuItem key={n} value={n}>
                                    {n}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            size="small"
                            label="Active = event"
                            placeholder="any event"
                            value={event}
                            error={!eventOk}
                            onChange={e => setEvent(e.target.value.trim())}
                            sx={{ width: 160 }}
                        />
                    </>
                }
            />
            <ReportStatus
                isLoading={isFetching}
                error={error}
                isEmpty={!!data && data.cohorts.length === 0}
                emptyIcon={<Replay />}
                emptyTitle="No identified visitors in this range"
                emptyDescription="Retention needs a visitor id (consented visitors)."
            >
                {data && (
                    <Card>
                        <CardContent sx={{ overflowX: 'auto' }}>
                            {data.truncated && (
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                    Too many visitors — cohorts cover a truncated sample.
                                </Alert>
                            )}
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Cohort</TableCell>
                                        <TableCell align="right">Visitors</TableCell>
                                        {Array.from({ length: data.periods }, (_, i) => (
                                            <TableCell key={i} align="center">
                                                {bucket === 'week' ? 'W' : 'D'}
                                                {i}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.cohorts.map(c => (
                                        <TableRow key={c.cohort}>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                {c.cohort}
                                            </TableCell>
                                            <TableCell align="right">{fmtNum(c.size)}</TableCell>
                                            {c.rates.map((r, i) => (
                                                <TableCell
                                                    key={i}
                                                    align="center"
                                                    title={`${fmtNum(c.retained[i])} visitors`}
                                                    sx={{
                                                        bgcolor: cellColor(r),
                                                        color: (r ?? 0) > 0.5 ? '#fff' : undefined,
                                                        fontSize: 12,
                                                    }}
                                                >
                                                    {r ? fmtPct(r) : ''}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                )}
            </ReportStatus>
        </Box>
    )
}
